/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import { Hidden } from '@coursera/cds-core';

import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { CalendarLoader } from 'bundles/degree-home/components/v3-dpm/components/Calendar';
import { CalendarDayPicker } from 'bundles/degree-home/components/v3-dpm/components/Calendar/CalendarDayPicker';
import { CalendarFilter } from 'bundles/degree-home/components/v3-dpm/components/Calendar/CalendarFilter';
import { CalendarItemsList } from 'bundles/degree-home/components/v3-dpm/components/Calendar/CalendarItemsList';
import { getCalendarEventsMap } from 'bundles/degree-home/components/v3-dpm/components/Calendar/utils';
import { Card } from 'bundles/program-loggedin-shared/components/Card';

import type { UseCalendarEventsQuery as UseCalendarEventsQueryData } from '../../lib/__queries__/__generated__/useCalendarEvents';
import useCalendarEvents from '../../lib/useCalendarEvents';

const styles = {
  card: () => css`
    overflow: hidden;
    padding: var(--cds-spacing-100) 0 var(--cds-spacing-400) 0;
  `,
};

type CardProps = {
  degreeId: string;
};

type ContentProps = {
  loading: boolean;
  data?: UseCalendarEventsQueryData;
  error: Error | undefined | null;
  refetch: () => void;
};

export const CalendarCardContent = ({ loading, data, error, refetch }: ContentProps) => {
  const { setRefetchCalendarItems, setCalendarItemsLoading, setCalendarEvents, setRegistrationDates } =
    useDegreeContext();

  useEffect(() => {
    const events = data?.Degree?.queryById?.events;

    if (events) {
      setCalendarEvents(getCalendarEventsMap(events));
    }

    setRefetchCalendarItems(() => refetch);
  }, [data, refetch, setCalendarEvents, setRefetchCalendarItems, setRegistrationDates]);

  useEffect(() => {
    setCalendarItemsLoading(loading);
    // We only want to call this effect when the loading changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (!data && loading) {
    return <CalendarLoader />;
  }

  if (error) {
    return null;
  }

  return (
    <Card css={styles.card}>
      <CalendarFilter />
      <CalendarDayPicker />
      <CalendarItemsList />
    </Card>
  );
};

export const CalendarCard = ({ degreeId }: CardProps) => {
  const { setDegreeId } = useDegreeContext();
  useEffect(() => {
    setDegreeId(degreeId);
  }, [degreeId, setDegreeId]);

  const calendarEventsGql = useCalendarEvents();

  if (!calendarEventsGql) {
    return null;
  }

  const { data, loading, error, refetch } = calendarEventsGql;

  return (
    <Hidden smDown>
      <CalendarCardContent loading={loading} data={data} error={error} refetch={refetch} />
    </Hidden>
  );
};
